class SubscriptionOnetimeWidget extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.cart = document.querySelector('cart-drawer');
    this.addToCartButton = this.querySelector('#addToCartButton');
    this.nextProductVariantId = this.addToCartButton.dataset.nextProductVariant.split('/').pop();
    this.sellingPlanInput = Object.assign(document.createElement('input'), { type: 'hidden', name: 'selling_plan' });
    this.purchaseTypes = Object.freeze({
      ONETIME: 'ONETIME',
      SUBSCRIPTION: 'SUBSCRIPTION',
      PREPAID: 'PREPAID',
    });
    this.onetimeButton = this.querySelector('#onetimeButton');
    this.subscriptionButton = this.querySelector('#subscriptionButton');
    this.prepaidButton = this.querySelector('#prepaidButton');
    this.frequencyPicker = this.subscriptionButton?.querySelector('.plan-picker') || null;
    this.prepaidPicker = this.prepaidButton?.querySelector('.plan-picker') || null;
    this.currentPurchaseType = this.purchaseTypes.ONETIME;
    this.alreadyDiscounted = this.dataset.alreadyDiscounted === 'true';
    this.hasPrepaid = this.dataset.hasPrepaid === 'true';
    this.variantsInformation = this.#buildVariantsInformation();
    this.variantChangeListener = this.#handleVariantChange.bind(this);
    this.testVariantListener = () => this.applyTestVariant();
    // Theme editor: selecting a USP block opens its purchase option so the USPs are visible
    this.blockSelectListener = (event) => {
      if (this.contains(event.target)) event.target.closest('.purchase_type')?.click();
    };
    this.outsideClickListener = (event) => {
      this.querySelectorAll('.plan-picker[open]').forEach((picker) => {
        if (!picker.contains(event.target)) this.#setPickerOpen(picker, false);
      });
    };
  }

  connectedCallback() {
    this.#addEventListeners();
    this.#preselectUpdate();
    this.#applyQueryParams();
    this.applyTestVariant();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.variantChangeListener);
    document.removeEventListener('subscription-widget:variant', this.testVariantListener);
    document.removeEventListener('click', this.outsideClickListener);
    document.removeEventListener('shopify:block:select', this.blockSelectListener);
  }

  /**
   * A/B/C test mode. A = one-time + subscribe, B = one-time + prepaid, C = all three.
   * Visibility is handled in CSS; this moves the selection off a card that has been hidden, and pre-selects
   * prepaid in C unless an ?m= link has already chosen the purchase option.
   */
  applyTestVariant() {
    const hasModeParam = new URLSearchParams(window.location.search).has('m');
    if (
      !this.hasAttribute('data-design-mode') &&
      !hasModeParam &&
      this.#getTestVariant() === 'C' &&
      this.#selectPurchaseType(this.prepaidButton)
    ) {
      return;
    }

    const selected = this.querySelector('.purchase_type[selected]');
    if (selected && this.#isPurchaseTypeVisible(selected)) return;

    if (selected === this.subscriptionButton) {
      this.#selectPurchaseType(this.prepaidButton) || this.#selectPurchaseType(this.onetimeButton);
    } else if (selected === this.prepaidButton) {
      this.#selectPurchaseType(this.subscriptionButton) || this.#selectPurchaseType(this.onetimeButton);
    } else {
      this.#selectPurchaseType(this.onetimeButton);
    }
  }

  #getTestVariant() {
    // The theme editor shows every option so each one can be configured
    if (this.hasAttribute('data-design-mode')) return 'C';

    const variant = document.documentElement.dataset.subWidgetVariant;
    return this.hasPrepaid && ['B', 'C'].includes(variant) ? variant : 'A';
  }

  #isPurchaseTypeVisible(purchaseType) {
    if (!purchaseType || purchaseType.classList.contains('hide_element')) return false;

    const variant = this.#getTestVariant();
    if (purchaseType === this.subscriptionButton) return variant !== 'B';
    if (purchaseType === this.prepaidButton) return variant !== 'A';
    return true;
  }

  #selectPurchaseType(purchaseType) {
    if (!this.#isPurchaseTypeVisible(purchaseType)) return false;
    purchaseType.click();
    return true;
  }

  #addEventListeners() {
    this.querySelectorAll('.purchase_type').forEach((purchaseType) => {
      purchaseType.addEventListener('click', () => {
        this.#clearClassOfAttribute('purchase_type', 'selected');
        purchaseType.setAttribute('selected', '');

        if (purchaseType === this.subscriptionButton) {
          this.sellingPlanInput.value =
            this.#getCheckedPlan(this.frequencyPicker)?.value || purchaseType.dataset.sellingPlanId;
          this.addToCartButton.setAttribute('data-discount-code', purchaseType.dataset.discountCode);
          this.currentPurchaseType = this.purchaseTypes.SUBSCRIPTION;
        } else if (purchaseType === this.prepaidButton) {
          this.sellingPlanInput.value =
            this.#getCheckedPlan(this.prepaidPicker)?.value || purchaseType.dataset.sellingPlanId;
          this.addToCartButton.removeAttribute('data-discount-code');
          this.currentPurchaseType = this.purchaseTypes.PREPAID;
        } else {
          this.addToCartButton.removeAttribute('data-discount-code');
          this.currentPurchaseType = this.purchaseTypes.ONETIME;
        }
      });
    });

    this.addToCartButton.addEventListener('click', () => {
      this.#addToCart(this.addToCartButton.dataset.discountCode || null);
    });

    this.#initPicker(this.frequencyPicker);
    this.#initPicker(this.prepaidPicker, () => this.#updatePrepaidPriceUI());

    if (this.frequencyPicker || this.prepaidPicker) {
      document.addEventListener('click', this.outsideClickListener);
    }

    document.addEventListener('variant:change', this.variantChangeListener);
    document.addEventListener('subscription-widget:variant', this.testVariantListener);

    if (this.hasAttribute('data-design-mode')) {
      document.addEventListener('shopify:block:select', this.blockSelectListener);
    }
  }

  #handleVariantChange(event) {
    this.#handleVariantUpdate(event.detail.variant.id);
  }

  #preselectUpdate() {
    const purchaseOption = this.querySelector('div.purchase_type[selected]');
    if (purchaseOption && purchaseOption === this.subscriptionButton) {
      this.#selectSubscriptionPurchaseType();
    }
  }

  #applyQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('m');

    if (mode === 'subscription') {
      this.#selectSubscriptionPurchaseType();
    } else if (mode === 'prepaid') {
      this.#selectPurchaseType(this.prepaidButton) || this.#selectSubscriptionPurchaseType();
    }

    const offerBadgeText = params.get('o');
    if (offerBadgeText) {
      this.#renderOfferBadge(offerBadgeText);
    }
  }

  #selectSubscriptionPurchaseType() {
    // Clicked even when hidden by the test, so applyTestVariant can move the selection to prepaid
    if (!this.subscriptionButton || this.subscriptionButton.classList.contains('hide_element')) return false;

    this.subscriptionButton.click();
    this.#preselectFrequency();
    return true;
  }

  #renderOfferBadge(text) {
    if (!this.frequencyPicker) return;

    const badge = document.createElement('span');
    badge.className = 'offer_badge';
    badge.textContent = text;
    this.frequencyPicker.insertAdjacentElement('afterend', badge);
  }

  #preselectFrequency() {
    const firstFrequency = this.frequencyPicker?.querySelector('input[type="radio"]');
    if (firstFrequency && !firstFrequency.checked) {
      firstFrequency.checked = true;
      this.sellingPlanInput.value = firstFrequency.value;
    }
  }

  async #addToCart(discountCode = null) {
    try {
      this.#setLoading(true);
      const formData = this.#buildFormData();
      const standardCart = new StandardCart(this, formData);
      const cart = await standardCart.addToCart();

      if (!this.alreadyDiscounted && cart) {
        const codes = cart.discount_codes.map((object) => object.code);
        const updatedCodes =
          this.currentPurchaseType == 'SUBSCRIPTION' && Boolean(discountCode)
            ? [...codes, discountCode]
            : codes.filter((code) => code != discountCode);

        const uniqueCodes = [...new Set(updatedCodes)];

        const allNewExist = uniqueCodes.every((code) => codes.includes(code));
        const allOldExist = codes.every((code) => uniqueCodes.includes(code));

        if (!(allNewExist && allOldExist)) {
          await standardCart.updateCartDiscounts(uniqueCodes);
        }
      }
    } catch (error) {
      console.log('Error while adding this product to cart: ', error);
    }
    this.#setLoading(false);
  }

  #isRecurringPurchase() {
    return (
      this.currentPurchaseType == this.purchaseTypes.SUBSCRIPTION ||
      this.currentPurchaseType == this.purchaseTypes.PREPAID
    );
  }

  #buildFormData() {
    this.#syncSelectedVariantFromProductInfo();
    this.#prepareForm();

    const formData = new FormData(this.form);
    if (this.#isRecurringPurchase() && this.nextProductVariantId) {
      formData.append('properties[_next_product_variant_id]', +this.nextProductVariantId);
    }
    return formData;
  }

  #prepareForm() {
    if (this.form.contains(this.sellingPlanInput)) {
      if (!this.#isRecurringPurchase()) {
        this.form.removeChild(this.sellingPlanInput);
      }
    } else {
      if (this.#isRecurringPurchase()) {
        this.form.appendChild(this.sellingPlanInput);
      }
    }
  }

  async refreshCart() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      document.dispatchEvent(
        new CustomEvent('cart:updated', {
          detail: { cart },
        }),
      );
      return cart;
    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
    return null;
  }

  #setLoading(loading) {
    const spinner = this.addToCartButton.querySelector('.loading__spinner');

    if (loading) {
      spinner.classList.remove('hidden');
      this.addToCartButton.disabled = true;
      this.addToCartButton.classList.add('loading');
    } else {
      spinner.classList.add('hidden');
      this.addToCartButton.disabled = false;
      this.addToCartButton.classList.remove('loading');
    }
  }

  #clearClassOfAttribute(className, attributeName) {
    this.querySelectorAll(`.${className}`).forEach((oneType) => {
      oneType.removeAttribute(attributeName);
    });
  }

  #handleVariantUpdate(variantId) {
    const matchingVariant = this.variantsInformation[variantId];
    if (!matchingVariant) return;

    this.nextProductVariantId = matchingVariant.nextProductVariantId;
    this.form.querySelector('[name="id"]').value = variantId;

    this.#updateVariantPriceUI(matchingVariant);
    this.#updatePickerOptions(
      this.frequencyPicker,
      matchingVariant.sellingPlans
        .filter((plan) => !plan.prepaid)
        .map(({ id, textContent }) => ({ id, textContent })),
    );
    this.#updatePickerOptions(
      this.prepaidPicker,
      matchingVariant.sellingPlans
        .filter((plan) => plan.prepaid)
        .map((plan) => ({ ...plan, comparePrice: matchingVariant.price * plan.deliveries })),
    );
    this.#updatePrepaidPriceUI();
    this.#syncSellingPlanInput();
  }

  #updateVariantPriceUI(matchingVariant) {
    this.querySelector('#subscriptionButton .extra_message').textContent = matchingVariant.saveThisAmountText;
    this.querySelector('#subscriptionButton .purchase_price').textContent =
      `£${(matchingVariant.discountedPrice / 100).toFixed(2)}`;
    this.querySelector('#subscriptionButton .strikethrough_price').textContent =
      `£${(matchingVariant.price / 100).toFixed(2)}`;
    this.querySelector('#onetimeButton .onetime_price').textContent = `£${(matchingVariant.price / 100).toFixed(2)}`;
  }

  /**
   * Expanding plan picker (snippets/subscription-plan-picker.liquid). Plans are visually hidden radios, so keyboard
   * and screen reader support come from the radio group. Collapsed, only the chosen plan shows and clicking it opens
   * the list; open, clicking any plan chooses it and closes the list.
   */
  #initPicker(picker, onChange) {
    if (!picker) return;

    const toggle = picker.querySelector('.plan-picker__toggle');

    picker.addEventListener('change', (event) => {
      this.sellingPlanInput.value = event.target.value;
      onChange?.();
      this.#setPickerOpen(picker, false);
    });

    picker.addEventListener('click', (event) => {
      if (!event.target.closest('.plan-picker__option') || event.target.matches('input')) return;
      this.#setPickerOpen(picker, !picker.hasAttribute('open'));
    });

    toggle?.addEventListener('click', () => {
      this.#setPickerOpen(picker, !picker.hasAttribute('open'));
    });

    picker.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && picker.hasAttribute('open')) {
        this.#setPickerOpen(picker, false);
        toggle?.focus();
      }
    });
  }

  #getCheckedPlan(picker) {
    return picker?.querySelector('input[type="radio"]:checked') || null;
  }

  #setPickerOpen(picker, open) {
    picker.toggleAttribute('open', open);
    picker.querySelector('.plan-picker__toggle')?.setAttribute('aria-expanded', String(open));
  }

  #updatePickerOptions(picker, sellingPlans) {
    const list = picker?.querySelector('.plan-picker__list');
    if (!list) return;

    const previousPlanId = this.#getCheckedPlan(picker)?.value;
    const inputName = list.querySelector('input')?.name || '';
    const hasPrevious = sellingPlans.some((plan) => String(plan.id) === previousPlanId);
    // Set by snippets/subscription-plan-picker.liquid from the block settings
    const { metaText, badgeText, badgeDeliveries } = picker.dataset;

    list.replaceChildren(
      ...sellingPlans.map((plan, index) => {
        const label = document.createElement('label');
        label.className = 'plan-picker__option';

        const input = Object.assign(document.createElement('input'), {
          type: 'radio',
          className: 'visually-hidden',
          name: inputName,
          value: plan.id,
          checked: hasPrevious ? String(plan.id) === previousPlanId : index === 0,
        });
        input.dataset.deliveries = plan.deliveries || 1;

        const details = Object.assign(document.createElement('span'), { className: 'plan-picker__details' });
        details.append(
          Object.assign(document.createElement('span'), {
            className: 'plan-picker__name',
            textContent: plan.textContent,
          }),
        );

        if (badgeText && Number(badgeDeliveries) === plan.deliveries) {
          details.append(
            Object.assign(document.createElement('span'), { className: 'plan-picker__badge', textContent: badgeText }),
          );
        }

        if (metaText && plan.price != null && plan.deliveries) {
          // Floored to match Liquid's divided_by
          const perDeliveryPrice = this.#formatMoney(Math.floor(plan.price / plan.deliveries));
          details.append(
            Object.assign(document.createElement('span'), {
              className: 'plan-picker__meta',
              textContent: metaText.replace('[price]', perDeliveryPrice),
            }),
          );
        }

        // Prices are optional, e.g. subscribe frequencies all cost the same so they don't show one
        if (plan.price == null) {
          label.append(input, details);
          return label;
        }

        input.dataset.price = plan.price;
        input.dataset.comparePrice = plan.comparePrice;

        const prices = Object.assign(document.createElement('span'), { className: 'plan-picker__prices' });
        const comparePrice = Object.assign(document.createElement('span'), {
          className: 'plan-picker__compare-price',
          textContent: this.#formatMoney(plan.comparePrice),
          hidden: plan.comparePrice <= plan.price,
        });
        const price = Object.assign(document.createElement('span'), {
          className: 'plan-picker__price',
          textContent: this.#formatMoney(plan.price),
        });
        prices.append(comparePrice, price);

        label.append(input, details, prices);
        return label;
      }),
    );
  }

  #updatePrepaidPriceUI() {
    const plan = this.#getCheckedPlan(this.prepaidPicker);
    if (!plan) return;

    const price = Number(plan.dataset.price) || 0;
    const comparePrice = Number(plan.dataset.comparePrice) || 0;

    this.prepaidButton.querySelector('.purchase_price').textContent = this.#formatMoney(price);

    const strikethroughPrice = this.prepaidButton.querySelector('.strikethrough_price');
    if (strikethroughPrice) {
      strikethroughPrice.textContent = this.#formatMoney(comparePrice);
      strikethroughPrice.hidden = comparePrice <= price;
    }
  }

  #formatMoney(cents) {
    return `£${(cents / 100).toFixed(2)}`;
  }

  #syncSellingPlanInput() {
    // Keep the selling plan in step with the rebuilt pickers after a variant change
    const picker =
      this.currentPurchaseType == this.purchaseTypes.SUBSCRIPTION
        ? this.frequencyPicker
        : this.currentPurchaseType == this.purchaseTypes.PREPAID
          ? this.prepaidPicker
          : null;
    const checkedPlan = this.#getCheckedPlan(picker);
    if (checkedPlan) this.sellingPlanInput.value = checkedPlan.value;
  }

  #buildVariantsInformation() {
    const variantsInformation = {};
    const siblingDataRoot = this.previousElementSibling?.matches('[data-role="subscription-variant-data"]')
      ? this.previousElementSibling
      : null;
    const dataRoot = this.querySelector('[data-role="subscription-variant-data"]') || siblingDataRoot;
    if (!dataRoot) return variantsInformation;

    dataRoot.querySelectorAll('[data-role="variant"]').forEach((variantNode) => {
      const variantId = variantNode.dataset.variantId;
      if (!variantId) return;

      variantsInformation[variantId] = {
        id: Number(variantId),
        price: Number(variantNode.dataset.price) || 0,
        discountedPrice: Number(variantNode.dataset.discountedPrice) || 0,
        nextProductVariantId: variantNode.dataset.nextProductVariantId || '',
        saveThisAmountText: variantNode.dataset.saveThisAmountText || '',
        sellingPlans: Array.from(variantNode.querySelectorAll('[data-role="selling-plan"]')).map((planNode) => ({
          id: Number(planNode.dataset.planId),
          textContent: planNode.dataset.planName || '',
          prepaid: planNode.dataset.prepaid === 'true',
          price: Number(planNode.dataset.price) || 0,
          perDeliveryPrice: Number(planNode.dataset.perDeliveryPrice) || 0,
          deliveries: Number(planNode.dataset.deliveries) || 0,
        })),
      };
    });

    return variantsInformation;
  }

  #syncSelectedVariantFromProductInfo() {
    const selectedVariantNode = this.closest('product-info')?.querySelector('variant-selects [data-selected-variant]');

    if (!selectedVariantNode?.textContent) return;

    try {
      const selectedVariant = JSON.parse(selectedVariantNode.textContent);
      const selectedVariantId = selectedVariant?.id;
      if (!selectedVariantId) return;

      this.form.querySelector('[name="id"]').value = selectedVariantId;
      this.#handleVariantUpdate(selectedVariantId);
    } catch (error) {
      console.warn('Failed to sync selected variant from product-info', error);
    }
  }
}

if (!customElements.get('subscription-onetime-widget')) {
  window.customElements.define('subscription-onetime-widget', SubscriptionOnetimeWidget);
}
